import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useSettings } from "@/context/SettingsContext";
import { Search, SlidersHorizontal, X } from "lucide-react";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();

  const collection = params.get("collection") || "";
  const sort = params.get("sort") || "newest";
  const availability = params.get("availability") || "";
  const stateFlag = params.get("state") || ""; // "", "new", "sale", "featured"
  const archiveLabel = settings?.archive?.nav_label || "Found a New Home";

  useEffect(() => { api.get("/collections").then((r) => setCollections(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (q) p.q = q;
    if (collection) p.collection = collection;
    if (sort) p.sort = sort;
    if (availability) p.availability = availability;
    if (stateFlag === "new") p.is_new_arrival = true;
    if (stateFlag === "sale") p.is_sale = true;
    if (stateFlag === "featured") p.featured = true;
    api.get("/products", { params: p }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [q, collection, sort, availability, stateFlag]);

  useEffect(() => { if (settings?.seo?.shop_title) document.title = settings.seo.shop_title; }, [settings]);

  const setFilter = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    setParams(np);
  };

  const [mobileFilters, setMobileFilters] = useState(false);
  const activeFilterCount = [collection, availability, stateFlag].filter(Boolean).length + (sort !== "newest" ? 1 : 0);

  // Filter body reused in sidebar (desktop) and drawer (mobile)
  const FilterBody = ({ onSelect }) => (
    <div className="space-y-6">
      <div className="relative">
        <input data-testid="shop-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="input-vintage pr-10" />
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-brass" size={16} />
      </div>
      <div>
        <div className="font-caption text-xs text-gold mb-3">State</div>
        <div className="space-y-1.5">
          {[["", "All"], ["new", "New Arrivals"], ["featured", "Featured"], ["sale", "On Sale"]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`filter-state-${v || "all"}`}
              onClick={() => { setFilter("state", v); onSelect?.(); }}
              className={`block text-left text-sm ${stateFlag === v ? "text-gold" : "text-parchment/70 hover:text-gold"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-caption text-xs text-gold mb-3">Collection</div>
        <div className="space-y-1.5">
          <button data-testid="filter-collection-all" onClick={() => { setFilter("collection", ""); onSelect?.(); }}
            className={`block text-left text-sm ${!collection ? "text-gold" : "text-parchment/70 hover:text-gold"}`}>All Pieces</button>
          {collections.map((c) => (
            <button key={c.id} data-testid={`filter-collection-${c.slug}`}
              onClick={() => { setFilter("collection", c.slug); onSelect?.(); }}
              className={`block text-left text-sm ${collection === c.slug ? "text-gold" : "text-parchment/70 hover:text-gold"}`}>{c.name}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-caption text-xs text-gold mb-3">Availability</div>
        <div className="space-y-1.5">
          {[["", "All"], ["in_stock", "In the Cabinet"], ["sold", archiveLabel]].map(([v, l]) => (
            <button key={v || "all"} data-testid={`filter-avail-${v || "all"}`}
              onClick={() => { setFilter("availability", v); onSelect?.(); }}
              className={`block text-left text-sm ${availability === v ? "text-gold" : "text-parchment/70 hover:text-gold"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="font-caption text-xs text-gold mb-3">Sort</div>
        <select data-testid="shop-sort" value={sort} onChange={(e) => setFilter("sort", e.target.value)} className="input-vintage">
          <option value="newest">Newest</option>
          <option value="price_asc">Price · Low to High</option>
          <option value="price_desc">Price · High to Low</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16" data-testid="shop-page">
      <div className="text-center mb-6 sm:mb-10">
        <p className="font-caption text-brass text-xs mb-2">The Cabinet</p>
        <h1 className="font-display text-4xl sm:text-6xl text-gold">Shop All</h1>
      </div>

      {/* Mobile filter trigger row */}
      <div className="lg:hidden flex items-center justify-between mb-4">
        <button data-testid="shop-mobile-filter-btn" onClick={() => setMobileFilters(true)}
          className="btn-outline text-xs py-2 px-3 flex items-center gap-2">
          <SlidersHorizontal size={14} /> Filter &amp; Sort
          {activeFilterCount > 0 && <span className="bg-gold text-forest rounded-full text-[10px] px-1.5 py-0.5 font-caption font-bold">{activeFilterCount}</span>}
        </button>
        <span className="font-caption text-brass text-[11px]">{products.length} {products.length === 1 ? "piece" : "pieces"}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-8">
        {/* Sidebar filters — desktop only */}
        <aside className="hidden lg:block lg:sticky lg:top-28 self-start">
          <FilterBody />
        </aside>

        {/* Grid */}
        <div>
          {loading ? (
            <div className="text-center py-24 text-parchment/60 font-caption text-sm">Gathering the finds…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-display text-2xl text-gold mb-2">Nothing quite fits that</p>
              <p className="text-parchment/60 italic">{settings?.empty_states?.search_empty || "Try loosening your search — the cabinet is deep."}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="shop-mobile-filter-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm p-5 overflow-y-auto"
               style={{ background: "#0F1D15", boxShadow: "-8px 0 24px rgba(0,0,0,0.4)" }}>
            <div className="flex justify-between items-center mb-6">
              <div className="font-display text-xl text-gold">Filter &amp; Sort</div>
              <button data-testid="shop-mobile-filter-close" onClick={() => setMobileFilters(false)} className="text-gold p-2"><X size={20} /></button>
            </div>
            <FilterBody onSelect={() => {}} />
            <div className="mt-8 space-y-2">
              <button data-testid="shop-mobile-filter-apply" onClick={() => setMobileFilters(false)} className="btn-gold w-full">Show {products.length} {products.length === 1 ? "piece" : "pieces"}</button>
              {activeFilterCount > 0 && (
                <button onClick={() => { setParams(new URLSearchParams()); setQ(""); }} className="btn-outline w-full text-xs">Clear all filters</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
